import { useEffect, useState } from "react";
import type { AccountInfo } from "../shared/accounts";
import { api } from "./api";
import { Modal } from "./Modal";
import "./account-usage.css";

export const storageLabel = (bytes: number) =>
  bytes >= 1024 ** 3
    ? `${Number((bytes / 1024 ** 3).toFixed(2))} GiB`
    : `${Number((bytes / 1024 ** 2).toFixed(1))} MiB`;

export function AccountUsage({ close }: { close: () => void }) {
  const [account, setAccount] = useState<AccountInfo | null>(null);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const refresh = () => api<AccountInfo>("/api/account").then(setAccount);
  useEffect(() => {
    void refresh().catch((e) => setError(e.message));
  }, []);
  return (
    <Modal title="Account & usage" close={close}>
      <div className="account-usage">
        {error && <p role="alert">{error}</p>}
        {!account ? (
          <p>Loading your account…</p>
        ) : (
          <>
            <p>
              <strong>
                {account.policy.tier === "trusted" ? "Trusted access" : "Free"}
              </strong>{" "}
              · No card required
            </p>
            <dl className="usage-totals">
              <div>
                <dt>Projects you own</dt>
                <dd>
                  {account.projects}
                  {account.policy.tier === "free"
                    ? ` / ${account.policy.project_limit}`
                    : " · no account limit"}
                </dd>
              </div>
              <div>
                <dt>Stored files</dt>
                <dd>
                  {storageLabel(account.storedBytes)}
                  {account.policy.tier === "free"
                    ? ` / ${storageLabel(account.policy.storage_limit)}`
                    : " · trusted allowance"}
                </dd>
              </div>
              {account.reservedBytes > 0 && (
                <div>
                  <dt>Reserved for uploads</dt>
                  <dd>{storageLabel(account.reservedBytes)}</dd>
                </div>
              )}
              <div>
                <dt>AI image and video generation</dt>
                <dd>Paused · no credits used</dd>
              </div>
            </dl>
            <p className="hint">
              Drawing, comments and export stay available when an allowance is
              full. Shared projects count towards their owner's storage. Files
              remain stored when removed from a sheet.
            </p>
            <h3>Need more? Contact me.</h3>
            {account.request?.state === "pending" ? (
              <p role="status">
                Your request is waiting for Rohan. You can keep working in your
                projects.
              </p>
            ) : (
              <>
                {account.request?.reply && (
                  <div className="account-reply">
                    <strong>Rohan's reply</strong>
                    <p>{account.request.reply}</p>
                  </div>
                )}
                <form
                  onSubmit={async (e) => {
                    e.preventDefault();
                    setBusy(true);
                    setError("");
                    try {
                      await api("/api/account/request", { message });
                      await refresh();
                      setMessage("");
                    } catch (e) {
                      setError((e as Error).message);
                    } finally {
                      setBusy(false);
                    }
                  }}
                >
                  <label>
                    What would you like to try?
                    <textarea
                      required
                      maxLength={1000}
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      placeholder="Tell me if you need more projects or storage."
                    />
                  </label>
                  <button
                    className="primary"
                    disabled={busy || !message.trim()}
                  >
                    {busy ? "Sending…" : "Send request to Rohan"}
                  </button>
                </form>
              </>
            )}
            <details>
              <summary>Account reference</summary>
              <code className="account-reference">
                {account.policy.user_id}
              </code>
            </details>
            {!!account.policy.is_operator && (
              <a href="/?admin=overview">Open Calque Admin</a>
            )}
          </>
        )}
      </div>
    </Modal>
  );
}
